import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Button, Dimmer, Icon, Label, Loader, Table, Transition } from 'semantic-ui-react'
import dayjs from 'dayjs'
import Layout from '~/components/Layout'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import CO2Service, { PurchasedMilkAmountOptionsType, UpdatePurchasedMilkAmountMonthsPayload } from '~/api/services/co2'

import CustomInput from '~/components/CustomInput'
import { handleErrors, capitalizeFirstLetter } from '~/utils/helpers'
import notify from '~/components/Notify'

import { FarmerGroupMonthsWrapper, StyledAccordionTitle, StyledNoResultsMessage } from '../PriceCalculator/style'

const AMOUNT_MAX = 1000000000

type EditableMonth = { month: string; amount: string }

const PurchasedAmount: React.FC = () => {
  const { t } = useTranslation()
  const [activeAccordionIndex, setActiveAccordionIndex] = useState(-1)
  const [editableMonths, setEditableMonths] = useState<EditableMonth[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const { data: optionsData, isLoading: isLoadingOptions } =
    CO2ClimateQuery.getPurchasedMilkAmountOptionsData()

  const years: { key: string; label: string }[] =
    (optionsData as PurchasedMilkAmountOptionsType)?.years ?? []

  const activeYearKey = activeAccordionIndex >= 0 && years[activeAccordionIndex] ? years[activeAccordionIndex].key : null

  const {
    data: monthsData,
    isLoading: isLoadingMonths,
    isFetching: isFetchingMonths,
    refetch: refetchMonths
  } = CO2ClimateQuery.getPurchasedMilkAmountMonths(activeYearKey ? { year: activeYearKey } : null)

  const months = Array.isArray(monthsData) ? monthsData : []

  useEffect(() => {
    if (months.length > 0) {
      setEditableMonths(months.map((m) => ({
        month: m.month,
        amount: m.amount != null && m.amount !== '' ? String(m.amount) : ''
      })))
    } else {
      setEditableMonths([])
    }
  }, [months.length, activeYearKey])

  async function handleSaveMonths() {
    const payload: UpdatePurchasedMilkAmountMonthsPayload = {
      months: editableMonths.map((m) => {
        const num = m.amount === '' ? 0 : parseInt(String(m.amount).replace(/,/g, ''), 10)
        const amount = Number.isNaN(num) ? 0 : Math.min(AMOUNT_MAX, Math.max(0, num))
        return { month: m.month, amount }
      })
    }
    setIsSaving(true)
    try {
      await CO2Service.updatePurchasedMilkAmountMonths(payload)
      notify(t('Saved successfully'), 'success')
      refetchMonths()
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsSaving(false)
    }
  }

  function handleAccordionClick(_e: any, titleProps: { index: number }) {
    const { index } = titleProps
    setActiveAccordionIndex(activeAccordionIndex === index ? -1 : index)
  }

  function handleChangeAmount(index: number, value: string | number) {
    setEditableMonths((prev) => {
      const next = [...prev]
      if (next[index]) next[index] = { ...next[index], amount: String(value ?? '') }
      return next
    })
  }

  function renderNoResultsMessage() {
    return <StyledNoResultsMessage><span>{t('No data')}</span></StyledNoResultsMessage>
  }

  function renderYearContent() {
    return (
      <FarmerGroupMonthsWrapper>
        <div className='actions'>
          <Button
            primary
            onClick={handleSaveMonths}
            disabled={editableMonths.length === 0 || isSaving}
            loading={isSaving}
          >
            {t('Update')}
          </Button>
        </div>
        <Dimmer active={isFetchingMonths} inverted>
          <Loader style={{ position: 'fixed' }} active={isFetchingMonths} />
        </Dimmer>
        <Table celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('Month')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Amount of milk (in kg)')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {editableMonths.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={2}>{renderNoResultsMessage()}</Table.Cell>
              </Table.Row>
            ) : (
              editableMonths.map((m, index) => (
                <Table.Row key={m.month}>
                  <Table.Cell>
                    <Label ribbon>{capitalizeFirstLetter(dayjs(m.month).format('MMM YYYY'))}</Label>
                  </Table.Cell>
                  <Table.Cell>
                    <CustomInput
                      value={m.amount}
                      onChange={(value) => handleChangeAmount(index, value ?? '')}
                      placeholder={t('Amount of milk (in kg)')}
                      suffix=' kg'
                      style={{ width: '100%' }}
                      allowDecimals={false}
                    />
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table>
      </FarmerGroupMonthsWrapper>
    )
  }

  function renderAccordions() {
    return years.map((year, index) => (
      <Accordion
        key={year.key + index}
        styled
        style={{ width: 'calc(100% - 40px)', margin: 10 }}
      >
        <Accordion.Title
          onClick={handleAccordionClick}
          index={index}
          active={activeAccordionIndex === index}
          style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}
        >
          <StyledAccordionTitle>{year.label}</StyledAccordionTitle>
          <Icon name={activeAccordionIndex === index ? 'minus' : 'plus'} />
        </Accordion.Title>
        <Transition
          visible={activeAccordionIndex === index && !isFetchingMonths}
          animation='slide down'
          duration={200}
        >
          <Accordion.Content
            active={activeAccordionIndex === index}
            content={activeAccordionIndex === index && renderYearContent()}
          />
        </Transition>
      </Accordion>
    ))
  }

  const isLoading = isLoadingOptions

  return (
    <Layout
      title={t('Purchased amounts')}
      isCo2Loader
      isBusy={isLoading}
      childContainerSize='auto'
      fontSizeHeader='18px'
    >
      {!isLoading && renderAccordions()}
    </Layout>
  )
}

export default PurchasedAmount
